import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Square, Trash2, Timer, Download, Filter, DollarSign, Users } from "lucide-react";
import { useTimer, formatDuration } from "@/context/TimerContext";
import { useAuth } from "@/context/AuthContext";

function isoWeek(d = new Date()) {
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - dayNum + 3);
  const firstThursday = dt.getTime();
  dt.setUTCMonth(0, 1);
  if (dt.getUTCDay() !== 4) dt.setUTCMonth(0, 1 + ((4 - dt.getUTCDay()) + 7) % 7);
  const wk = 1 + Math.ceil((firstThursday - dt) / (7 * 86400000));
  return `${new Date(firstThursday).getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}

export default function TimeTrackerPage() {
  const { user } = useAuth();
  const { active, elapsed, start, stop, discard } = useTimer();
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [totals, setTotals] = useState({ total_seconds: 0, total_hours: 0, total_billable: 0 });
  const [week, setWeek] = useState(isoWeek());
  const [scope, setScope] = useState("mine");
  const [loading, setLoading] = useState(true);
  const isAdmin = ["owner", "admin"].includes(user?.workspace_role) || user?.is_superadmin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: e }, { data: r }] = await Promise.all([
        api.get("/time/entries", { params: { scope, week } }),
        api.get("/time/reports/weekly", { params: { week } }),
      ]);
      setEntries(e.entries || []);
      setTotals({ total_seconds: e.total_seconds, total_hours: e.total_hours, total_billable: e.total_billable });
      setReport(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [scope, week]);

  useEffect(() => { load(); }, [load, active?.id]);

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/time/entries/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const editEntry = async (row) => {
    const desc = window.prompt("Description", row.description || "");
    if (desc == null) return;
    try {
      await api.patch(`/time/entries/${row.id}`, { description: desc });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/time/reports/csv", { params: { week }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `time_${week}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="time-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label text-[var(--peak-accent)]">TIME TRACKING</div>
          <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mt-1">TIMESHEETS</h1>
          <p className="text-[var(--peak-muted)] mt-2">Track billable minutes against clients. One timer at a time — persists across pages.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="section-label">Week</label>
            <input
              data-testid="time-week"
              type="week"
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              className="brutalist-input"
            />
          </div>
          <button
            data-testid="time-csv-btn"
            onClick={downloadCsv}
            className="bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-bold uppercase tracking-widest px-4 py-3 text-xs inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </header>

      {/* Active timer card (jumps out when running) */}
      {active && (
        <div className="mb-6 border-2 border-[#008A27] bg-white p-5 shadow-[4px_4px_0_0_var(--peak-shadow)]" data-testid="time-active-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="section-label text-[#008A27]">CURRENTLY TRACKING</div>
              <div className="font-mono font-black text-5xl tracking-tighter tabular-nums mt-1">
                {formatDuration(elapsed)}
              </div>
              <div className="text-sm mt-1 text-[var(--peak-muted)]">
                {active.contact_name || active.task || "Working"}
                {active.billable ? <> · <span className="text-[#008A27] font-bold">${((elapsed / 3600) * active.hourly_rate).toFixed(2)}</span> at ${active.hourly_rate}/hr</> : null}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={stop}
                data-testid="active-stop"
                className="px-4 py-3 text-xs font-bold uppercase tracking-widest bg-[#008A27] hover:bg-black text-white inline-flex items-center gap-2"
              >
                <Square className="w-3.5 h-3.5" /> Stop & log
              </button>
              <button
                onClick={() => { if (window.confirm("Discard this session?")) discard(); }}
                data-testid="active-discard"
                className="px-4 py-3 text-xs font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[#E50000] hover:border-[#E50000] hover:text-white"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="time-kpis">
        <Kpi icon={Timer} label="Total tracked" value={`${totals.total_hours.toFixed(2)} h`} />
        <Kpi icon={DollarSign} label="Billable" value={`$${totals.total_billable.toFixed(2)}`} accent />
        <Kpi icon={Users} label="Entries" value={entries.length} />
        <Kpi icon={Filter} label="Week" value={week} />
      </div>

      {/* Scope tabs (only admins see 'All members') */}
      <div className="flex items-center gap-2 mb-6 border-b border-[var(--peak-border)]">
        {["mine", ...(isAdmin ? ["all"] : [])].map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            data-testid={`time-scope-${s}`}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px ${
              scope === s ? "border-[var(--peak-accent)] text-[var(--peak-ink)]" : "border-transparent text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
            }`}
          >
            {s === "mine" ? "My time" : "All members"}
          </button>
        ))}
      </div>

      {/* Entries table */}
      <div className="border-2 border-[var(--peak-ink)] bg-white overflow-x-auto mb-8" data-testid="time-entries">
        <table className="w-full text-sm">
          <thead className="bg-[var(--peak-bg)] border-b-2 border-[var(--peak-ink)]">
            <tr className="text-left">
              <Th>Started</Th><Th>Client</Th><Th>Task</Th>
              <Th className="text-right">Duration</Th><Th className="text-right">Billable</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--peak-muted)] text-xs">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center">
                <Timer className="w-6 h-6 mx-auto text-[var(--peak-muted)] mb-2" />
                <div className="section-label">No entries this week</div>
                <div className="text-xs text-[var(--peak-muted)] mt-1">Start a timer to see it here.</div>
              </td></tr>
            ) : entries.map((r) => {
              const dur = r.status === "running" ? "—" : formatDuration(r.seconds);
              const amount = r.billable && r.status === "stopped" ? ((r.seconds / 3600) * r.hourly_rate).toFixed(2) : null;
              return (
                <tr key={r.id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-bg)]" data-testid={`time-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-[10px]">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs">{r.contact_name || <span className="text-[var(--peak-muted)]">—</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-semibold">{r.task || <span className="text-[var(--peak-muted)]">Untitled</span>}</div>
                    {r.description && <div className="text-[10px] text-[var(--peak-muted)] truncate max-w-[280px]">{r.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {r.status === "running" ? <span className="text-[#008A27] font-bold">● LIVE</span> : dur}
                  </td>
                  <td className="px-4 py-3 text-right font-heading font-black">
                    {amount ? `$${amount}` : <span className="text-[var(--peak-muted)] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "stopped" && (
                      <>
                        <button onClick={() => editEntry(r)} data-testid={`time-edit-${r.id}`} className="text-[10px] font-bold uppercase tracking-widest text-[var(--peak-blue-deep)] hover:text-[var(--peak-accent)] mr-2">Edit</button>
                        <button onClick={() => removeEntry(r.id)} data-testid={`time-delete-${r.id}`} className="text-[10px] font-bold uppercase tracking-widest text-[#E50000] inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Weekly by-client + by-user summaries */}
      {report && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SummaryCard title="Hours by client" rows={report.by_client.map((r) => ({
            label: r.contact_name, seconds: r.seconds, billable: r.billable,
          }))} />
          <SummaryCard title="Hours by teammate" rows={report.by_user.map((r) => ({
            label: r.user_name || r.user_email, seconds: r.seconds, billable: r.billable,
          }))} />
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[var(--peak-muted)] ${className}`}>{children}</th>;
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border-2 p-4 ${accent ? "border-[var(--peak-accent)] bg-[var(--peak-ink)] text-white" : "border-[var(--peak-ink)] bg-white"}`}>
      <div className={`flex items-center gap-2 ${accent ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)]"}`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-heading font-black text-2xl tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function SummaryCard({ title, rows }) {
  return (
    <div className="border-2 border-[var(--peak-ink)] bg-white" data-testid={`time-summary-${title.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="px-4 py-3 border-b-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] section-label">{title}</div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-[var(--peak-muted)]">No data.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[var(--peak-border)] last:border-b-0">
                <td className="px-4 py-2 text-xs truncate max-w-[240px]">{r.label}</td>
                <td className="px-4 py-2 text-xs text-right font-mono tabular-nums">{formatDuration(r.seconds)}</td>
                <td className="px-4 py-2 text-right font-heading font-black">${r.billable.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
